import type { AdminOrderResponse } from '@inflip/contracts/admin';
import {
  DepositReturnKind,
  OrderStatus,
  PayoutClawbackStatus,
  RefundStatus,
  TERMINATION_KIND_LABELS,
  TerminationRefundMode,
  ViolationReportResolution,
} from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { ConfirmDepositDialog, REFUND_ACCOUNT_NOTE, refundBankOf } from './confirm-deposit-dialog';
import { DepositReturnsSection } from './deposit-returns-section';
import { EvidenceGallery } from './evidence-gallery';
import { LateDepositDialog } from './late-deposit-dialog';
import {
  GroupOrdersBox,
  OrderConfirmPaymentDialog,
  pendingGroupMembers,
} from './order-confirm-payment-dialog';
import { TerminateContractDialog } from './terminate-contract-dialog';
import { TerminationBadge } from './termination-badge';
import {
  BankSelect,
  Button,
  ConfirmDialog,
  ErrorBanner,
  PromptDialog,
  Section,
  inputCls,
} from './ui';
import { api } from '../lib/api-client';
import { PAYOUT_CLAWBACK_STATUS_META, REFUND_STATUS_META } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';

interface Props {
  order: AdminOrderResponse;
  token: string;
  /** 액션 성공 후 주문 다시 로드 — 끝날 때까지 버튼을 막아 둔다 */
  onChanged(): Promise<unknown>;
}

/**
 * 주문 운영 액션: 입금 확인 / 취소 / 환불 워크플로(요청→승인→지급완료).
 */
/** 서버 오류를 창 안에 직접 보여 주는 창 — 실패해도 입력을 잃지 않게 열어 둔다 */
const ERROR_IN_DIALOG = new Set(['confirmPayment', 'confirmAddon', 'lateDeposit', 'terminate']);

/** 판매자 회수를 아직 확인하지 않은 환불 */
function outstanding(r: AdminOrderResponse['refunds'][number]): boolean {
  return (
    r.clawback?.status === PayoutClawbackStatus.PENDING ||
    r.clawback?.status === PayoutClawbackStatus.OVERDUE
  );
}

export function OrderActionsPanel({ order, token, onChanged }: Props) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    | null
    | { kind: 'cancel' }
    | { kind: 'dismissDispute' }
    | {
        kind: 'resolveReport';
        reportId: string;
        resolution:
          | typeof ViolationReportResolution.ACTIONED
          | typeof ViolationReportResolution.DISMISSED;
      }
    | { kind: 'terminate' }
    | { kind: 'cancelTermination' }
    | { kind: 'lateDeposit' }
    | { kind: 'rejectRefund'; refundId: string }
    | { kind: 'revokeRefund'; refundId: string; amount: number }
    // 돈이 움직이거나 되돌리기 어려운 액션은 원클릭 금지 — 확인 다이얼로그를 거친다
    | { kind: 'confirmPayment' }
    | { kind: 'confirmAddon'; paymentId: string; amount: number; depositReference: string | null }
    | { kind: 'approveRefund'; refundId: string; amount: number }
    | { kind: 'completeRefund'; refundId: string; amount: number }
    | { kind: 'collectClawback'; clawbackId: string; amount: number }
  >(null);

  // 환불 요청 폼 — 평소엔 접어두고 버튼으로 연다
  const [refundFormOpen, setRefundFormOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState(order.totalAmount);
  const [refundReason, setRefundReason] = useState('');
  // 환불 계좌 — 구매자가 등록한 환불 계좌로 미리 채운다
  const [bank, setBank] = useState(order.buyerRefundAccount?.bankName ?? '');
  const [accountNumber, setAccountNumber] = useState(order.buyerRefundAccount?.accountNumber ?? '');
  const [holder, setHolder] = useState(order.buyerRefundAccount?.holderName ?? '');

  /** 실패하면 오류 문구, 성공하면 null — 입금 반환 창은 이걸 보고 열어 둘지 정한다 */
  async function run(fn: () => Promise<unknown>): Promise<string | null> {
    if (pending) return '이미 처리 중이에요.';
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      // 사이드바 빨간 점도 바로 다시 센다
      notifyPendingWorkChanged();
      // 지금 상태가 그려질 때까지 버튼을 막아 둔다
      await onChanged();
      return null;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      // 그사이 다른 어드민·구매자가 상태를 바꿔 거절됐을 수 있다 — 지금 상태로 다시 그린다.
      // 오류를 창 안에 보여 주는 입력 창(입금 확인·해지 등)은 열어 두고, 나머지는 닫아 아래 오류가 보이게
      setDialog((d) => (d && ERROR_IN_DIALOG.has(d.kind) ? d : null));
      await onChanged();
      return message;
    } finally {
      setPending(false);
    }
  }

  const canConfirmPayment =
    order.status === OrderStatus.PAYMENT_PENDING && order.payment?.status === 'PENDING';
  // 추가금 — PAID 주문에 입금 대기 중인 ADDON 결제가 있으면 확인 대상 (상태 전이 없음)
  const pendingAddon =
    order.status === OrderStatus.PAID
      ? order.addonPayments.find((p) => p.status === 'PENDING')
      : undefined;
  const canCancel =
    order.status === OrderStatus.ORDER_CREATED || order.status === OrderStatus.PAYMENT_PENDING;
  const groupMembers = pendingGroupMembers(order);
  const grouped = groupMembers.length > 1;
  // 결제 금액이 0원인 주문(로열티 계약 등)은 돌려줄 돈이 없어 환불 요청을 만들지 않는다
  const canRequestRefund =
    order.totalAmount > 0 &&
    (
      [
        OrderStatus.PAID,
        OrderStatus.LICENSE_ISSUED,
        OrderStatus.SETTLEMENT_PENDING,
        OrderStatus.DISPUTED,
        OrderStatus.PAYOUT_COMPLETED,
        OrderStatus.TERMINATED, // 해지 뒤 차지백 등
      ] as string[]
    ).includes(order.status);

  // 계약 해지 — 발급 이후 계약, 또는 선불 0원 계약은 발급 전에도 (발급 전 유상 주문은 전액 환불로)
  const canTerminate =
    (
      [
        OrderStatus.LICENSE_ISSUED,
        OrderStatus.SETTLEMENT_PENDING,
        OrderStatus.PAYOUT_COMPLETED,
        OrderStatus.DISPUTED,
      ] as string[]
    ).includes(order.status) ||
    (order.status === OrderStatus.PAID && order.totalAmount === 0);
  const termination = order.status === OrderStatus.TERMINATED ? order.termination : null;
  const depositReturns = order.depositReturns ?? [];
  // 취소된 주문에 늦게 들어온 입금 — 되살리기(기한 만료 취소만) 또는 받은 금액 반환.
  // 이미 입금 반환을 만든 주문(부족 입금 반환으로 취소 등)은 그 입금을 처리한 것이라 띄우지 않는다
  const canHandleLateDeposit =
    order.status === OrderStatus.CANCELLED && !!order.payment && depositReturns.length === 0;

  const disputed = order.status === OrderStatus.DISPUTED;
  // 처리 중인 판매자 위반 신고 (주문당 하나) — 주문 상태와 별개
  const openReport = order.violationReports?.find((r) => !r.resolution);
  const refundInProgress = order.refunds.some(
    (r) => r.status === RefundStatus.REQUESTED || r.status === RefundStatus.APPROVED,
  );

  if (
    !canConfirmPayment &&
    !canCancel &&
    !pendingAddon &&
    !canRequestRefund &&
    !disputed &&
    !openReport &&
    !canTerminate &&
    !termination &&
    !canHandleLateDeposit &&
    depositReturns.length === 0 &&
    !order.revivedAt &&
    order.refunds.length === 0
  ) {
    return null;
  }

  return (
    <Section title="운영 액션">
      {/* 분쟁 처리 — 환불로 끝내려면 아래 '환불 직접 만들기', 근거가 없거나 합의됐으면 기각해 정산을 다시 진행
          (정산 지급 뒤 분쟁은 기각하면 지급 완료로 돌아가고, 환불은 판매자 회수 뒤에) */}
      {disputed && (
        <div className="mb-5 rounded-lg bg-rose-50 px-4 py-3.5">
          <p className="text-sm font-bold text-rose-700">분쟁 처리 중</p>
          {order.disputeReason && (
            <p className="mt-1 whitespace-pre-wrap text-sm">{order.disputeReason}</p>
          )}
          {order.disputedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              접수 {formatDateTime(order.disputedAt)}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              disabled={pending || refundInProgress}
              onClick={() => setDialog({ kind: 'dismissDispute' })}
            >
              {order.settlementPaidOut ? '분쟁 기각' : '분쟁 기각 · 정산 진행'}
            </Button>
            <p className="text-xs text-muted-foreground">
              {refundInProgress
                ? '진행 중인 환불을 먼저 처리해야 기각할 수 있어요.'
                : order.settlementPaidOut
                  ? '정산이 이미 지급된 뒤 열린 분쟁이에요. 환불하려면 아래에서 환불을 만들면 판매자 회수(14일)가 함께 열리고, 회수를 확인한 뒤 지급할 수 있어요. 쓴 기간은 인정하고 끝내려면 계약 해지를 쓰세요.'
                  : '전액 환불로 끝내려면 아래에서 환불을 만들어 지급까지 처리하세요(거래를 무름). 쓴 기간은 인정하고 끝내려면 계약 해지를 쓰세요. 부분 환불 뒤 기각하면 남은 금액만 정산돼요.'}
            </p>
          </div>
        </div>
      )}

      {/* 판매자 위반 신고 — 주문 상태·정산은 그대로. 필요하면 라이선스 탭의 사용 중지나 아래 계약 해지(구매자 위반),
          확인을 마치면 조치 완료, 근거가 없으면 기각 */}
      {openReport && (
        <div className="mb-5 rounded-lg bg-amber-50 px-4 py-3.5">
          <p className="text-sm font-bold text-amber-700">판매자 위반 신고 처리 중</p>
          <p className="mt-1 whitespace-pre-wrap text-sm">{openReport.reason}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            접수 {formatDateTime(openReport.createdAt)}
          </p>
          {openReport.evidenceCount > 0 && (
            <EvidenceGallery orderId={order.id} token={token} reportId={openReport.id} />
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              disabled={pending}
              onClick={() =>
                setDialog({
                  kind: 'resolveReport',
                  reportId: openReport.id,
                  resolution: ViolationReportResolution.ACTIONED,
                })
              }
            >
              조치 완료
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={pending}
              onClick={() =>
                setDialog({
                  kind: 'resolveReport',
                  reportId: openReport.id,
                  resolution: ViolationReportResolution.DISMISSED,
                })
              }
            >
              신고 기각
            </Button>
            <p className="text-xs text-muted-foreground">
              조사하는 동안 막으려면 라이선스 탭에서 사용 중지를, 계약을 끝내려면 아래 계약
              해지(구매자 위반)를 쓰세요. 해지하면 이 신고도 함께 닫혀요.
            </p>
          </div>
        </div>
      )}

      {order.revivedAt && (
        <div className="mb-5 rounded-lg bg-emerald-50 px-4 py-3.5">
          <p className="text-sm font-bold text-emerald-700">만료 후 입금으로 되살린 주문</p>
          <p className="mt-1 text-sm">{order.reviveReason}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            되살림 {formatDateTime(order.revivedAt)}
          </p>
        </div>
      )}

      {canHandleLateDeposit && (
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant="secondary"
            disabled={pending}
            onClick={() => {
              setError(null);
              setDialog({ kind: 'lateDeposit' });
            }}
          >
            늦게 들어온 입금 처리
          </Button>
          <p className="text-xs text-muted-foreground">
            취소된 뒤 입금이 들어왔으면 되살려 결제를 확인하거나, 받은 금액을 돌려줘요.
          </p>
        </div>
      )}

      {termination && (
        <div className="mb-5 rounded-lg bg-rose-50 px-4 py-3.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-bold text-rose-700">계약 해지됨</p>
            <TerminationBadge termination={termination} />
          </div>
          <p className="mt-1 text-sm">
            <span className="font-semibold">{TERMINATION_KIND_LABELS[termination.kind]}</span> ·{' '}
            <span className="whitespace-pre-wrap">{termination.reason}</span>
          </p>
          {termination.refundMode !== TerminationRefundMode.NONE && (
            <p className="mt-1 text-xs text-muted-foreground">
              해지와 함께 선불 {termination.refundAmount.toLocaleString('ko-KR')}원을 돌려줘요.
              {termination.refundStatus === RefundStatus.APPROVED &&
                (order.refunds.find((r) => r.id === termination.refundId)?.clawback?.status ===
                  PayoutClawbackStatus.PENDING ||
                order.refunds.find((r) => r.id === termination.refundId)?.clawback?.status ===
                  PayoutClawbackStatus.OVERDUE
                  ? ' 정산이 이미 지급돼 판매자에게서 먼저 돌려받아요. 회수를 확인한 뒤 송금하고 지급 완료 처리를 눌러 주세요.'
                  : ' 송금한 뒤 아래 환불 내역에서 지급 완료 처리를 눌러 주세요.')}
            </p>
          )}
          {termination.executedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              해지 {formatDateTime(termination.executedAt)} (이 시각부터 라이선스 사용 불가)
            </p>
          )}
          {/* 해지 취소 — 해지와 함께 정한 환불을 지급하기 전에만 (서버가 그 밖의 경우도 다시 막는다) */}
          {termination.refundStatus !== RefundStatus.COMPLETED && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                variant="secondary"
                disabled={pending}
                onClick={() => {
                  setError(null);
                  setDialog({ kind: 'cancelTermination' });
                }}
              >
                해지 취소
              </Button>
              <p className="text-xs text-muted-foreground">
                착오로 해지했다면 송금 전에 되돌릴 수 있어요.
              </p>
            </div>
          )}
        </div>
      )}

      {canTerminate && (
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant="danger"
            disabled={pending || refundInProgress}
            onClick={() => {
              setError(null);
              setDialog({ kind: 'terminate' });
            }}
          >
            계약 해지
          </Button>
          <p className="text-xs text-muted-foreground">
            {refundInProgress
              ? '진행 중인 환불을 먼저 처리해야 해지할 수 있어요.'
              : '지금부터 계약을 끝내요. 해지일까지 쓴 건 유효하고, 라이선스는 해지일이 마지막 사용일이 돼요. 선불은 비환불·부분·전액 중에서 함께 골라요.'}
          </p>
        </div>
      )}

      {/* 라이선스 발급은 판매자의 일 — 어드민은 입금 확인/취소/환불만 다룬다 */}
      {(canConfirmPayment || canCancel || pendingAddon) && (
        <div className="flex flex-wrap gap-2">
          {canConfirmPayment && order.payment && (
            <Button
              size="sm"
              disabled={pending}
              onClick={() => setDialog({ kind: 'confirmPayment' })}
            >
              입금 확인 ({order.payment.amount.toLocaleString('ko-KR')}원)
            </Button>
          )}

          {pendingAddon && (
            <Button
              size="sm"
              disabled={pending}
              onClick={() =>
                setDialog({
                  kind: 'confirmAddon',
                  paymentId: pendingAddon.id,
                  amount: pendingAddon.amount,
                  depositReference: pendingAddon.depositReference,
                })
              }
            >
              추가금 입금 확인 ({pendingAddon.amount.toLocaleString('ko-KR')}원)
            </Button>
          )}

          {canCancel && (
            <Button
              size="sm"
              variant="danger"
              disabled={pending}
              onClick={() => setDialog({ kind: 'cancel' })}
            >
              주문 취소
            </Button>
          )}
        </div>
      )}

      {canRequestRefund && (
        <div
          className={
            canConfirmPayment || canCancel
              ? 'mt-5 space-y-2.5 border-t border-border/60 pt-4'
              : 'space-y-2.5'
          }
        >
          {!refundFormOpen ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm" variant="secondary" onClick={() => setRefundFormOpen(true)}>
                환불 직접 만들기
              </Button>
              <p className="text-xs text-muted-foreground">
                구매자 대신 환불을 만들어요. 전액 환불은 거래를 처음부터 무르고(라이선스 회수), 부분
                환불은 계약을 그대로 두고 금액만 돌려줘요.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm font-bold">환불 직접 만들기</p>
              <p className="text-xs text-muted-foreground">
                구매자 대신 환불 요청을 만들어요. 무통장 환불이라 수취 계좌가 꼭 필요해요.
                {order.buyerRefundAccount
                  ? ` 계좌는 ${REFUND_ACCOUNT_NOTE}.`
                  : ' 구매자가 등록한 환불 계좌가 없어 직접 받아 적어야 해요.'}
              </p>
              <div className="flex flex-wrap gap-2">
                {/* 금액은 천 단위 쉼표로 보이고, 입력은 숫자만 받는다 (계약 해지 창과 같은 방식) */}
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="환불 금액"
                  className={`${inputCls} w-32 text-right`}
                  value={refundAmount ? refundAmount.toLocaleString('ko-KR') : ''}
                  onChange={(e) => setRefundAmount(Number(e.target.value.replace(/[^\d]/g, '')))}
                />
                <input
                  className={`${inputCls} w-40`}
                  placeholder="사유"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                />
                <BankSelect className="w-36" value={bank} onChange={setBank} />
                <input
                  className={`${inputCls} w-36`}
                  placeholder="계좌번호"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
                <input
                  className={`${inputCls} w-24`}
                  placeholder="예금주"
                  value={holder}
                  onChange={(e) => setHolder(e.target.value)}
                />
                <Button
                  variant="danger"
                  disabled={pending || !refundReason || !bank || !accountNumber || !holder}
                  onClick={() =>
                    void run(async () => {
                      await api.createRefund(token, order.id, {
                        amount: refundAmount,
                        reason: refundReason,
                        bankAccount: { bank, accountNumber, holder },
                      });
                      setRefundFormOpen(false);
                    })
                  }
                >
                  환불 요청
                </Button>
                <Button
                  variant="secondary"
                  disabled={pending}
                  onClick={() => setRefundFormOpen(false)}
                >
                  닫기
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {order.refunds.length > 0 && (
        <div className="mt-5 border-t border-border/60 pt-4">
          <p className="text-sm font-bold">환불 내역</p>
          <div className="mt-1 divide-y divide-border/40">
            {order.refunds.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 py-2.5 text-sm">
                <span className={`text-xs font-bold ${REFUND_STATUS_META[r.status].cls}`}>
                  {REFUND_STATUS_META[r.status].label}
                </span>
                <span className="font-bold tabular-nums">{r.amount.toLocaleString('ko-KR')}원</span>
                <span className="text-xs text-muted-foreground">{r.reason}</span>
                {/* 환불 번호 — 누르면 이 환불의 처리 기록 */}
                <Link
                  to="/audit-logs"
                  search={{ q: r.id }}
                  title="이 환불의 처리 기록 보기"
                  className="font-mono text-xs text-brand hover:underline"
                >
                  {r.id.slice(0, 8)} 처리 기록
                </Link>
                {r.bankAccount && (
                  <span className="text-xs text-muted-foreground">
                    → {r.bankAccount.bank} {r.bankAccount.accountNumber} ({r.bankAccount.holder})
                  </span>
                )}
                <span className="ml-auto flex gap-1.5">
                  {r.status === RefundStatus.REQUESTED && (
                    <>
                      <Button
                        size="sm"
                        disabled={pending}
                        onClick={() =>
                          setDialog({ kind: 'approveRefund', refundId: r.id, amount: r.amount })
                        }
                      >
                        승인
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={pending}
                        onClick={() => setDialog({ kind: 'rejectRefund', refundId: r.id })}
                      >
                        거절
                      </Button>
                    </>
                  )}
                  {r.status === RefundStatus.APPROVED && (
                    <>
                      {/* 해지와 함께 정한 환불은 해지 기록과 어긋나 승인을 취소할 수 없다 */}
                      {order.termination?.refundId !== r.id && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={pending}
                          onClick={() =>
                            setDialog({ kind: 'revokeRefund', refundId: r.id, amount: r.amount })
                          }
                        >
                          승인 취소
                        </Button>
                      )}
                      {outstanding(r) && (
                        <Button
                          size="sm"
                          disabled={pending}
                          onClick={() =>
                            setDialog({
                              kind: 'collectClawback',
                              clawbackId: r.clawback!.id,
                              amount: r.clawback!.amount,
                            })
                          }
                        >
                          회수 확인
                        </Button>
                      )}
                      {/* 정산 지급 뒤 환불은 판매자 회수를 확인해야 송금한다 — 플랫폼이 먼저 떠안지 않는다 */}
                      <Button
                        size="sm"
                        variant={outstanding(r) ? 'secondary' : undefined}
                        disabled={pending || outstanding(r)}
                        title={outstanding(r) ? '판매자 회수를 먼저 확인해 주세요' : undefined}
                        onClick={() =>
                          setDialog({ kind: 'completeRefund', refundId: r.id, amount: r.amount })
                        }
                      >
                        지급 완료 처리
                      </Button>
                    </>
                  )}
                </span>
                {r.clawback && (
                  <p className="w-full text-xs text-muted-foreground">
                    <span
                      className={`font-bold ${PAYOUT_CLAWBACK_STATUS_META[r.clawback.status].cls}`}
                    >
                      {PAYOUT_CLAWBACK_STATUS_META[r.clawback.status].label}
                    </span>{' '}
                    {r.clawback.amount.toLocaleString('ko-KR')}원
                    {outstanding(r) && ` · 기한 ${formatDateTime(r.clawback.dueAt)}`}
                    {r.clawback.status === PayoutClawbackStatus.OVERDUE &&
                      (r.clawback.suspendedSeller
                        ? ' · 판매자 접속 정지됨 (회수 확인 시 풀림)'
                        : ' · 판매자는 이미 다른 사유로 정지된 상태')}
                    {r.clawback.collectedAt &&
                      ` · ${formatDateTime(r.clawback.collectedAt)} 확인${r.clawback.collectionNote ? ` — ${r.clawback.collectionNote}` : ''}`}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <DepositReturnsSection
        returns={depositReturns}
        busy={pending}
        onComplete={(id, note) => run(() => api.completeDepositReturn(token, id, { note }))}
        onUpdateAccount={(id, bankAccount, reason) =>
          run(() => api.updateDepositReturnAccount(token, id, { bankAccount, reason }))
        }
      />

      {error && (
        <div className="mt-4">
          <ErrorBanner message={error} />
        </div>
      )}

      {dialog?.kind === 'confirmPayment' && order.payment && (
        <OrderConfirmPaymentDialog
          order={{ ...order, payment: order.payment }}
          token={token}
          onDone={async (ok) => {
            // 지금 상태가 그려진 뒤에 창을 닫는다 — 그때까지 확인 버튼은 막혀 있다
            await onChanged();
            if (ok) {
              setDialog(null);
              notifyPendingWorkChanged();
            }
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'confirmAddon' && (
        <ConfirmDepositDialog
          title="추가금 입금 확인"
          defaultBankAccount={
            order.buyerRefundAccount ? refundBankOf(order.buyerRefundAccount) : null
          }
          expectedAmount={dialog.amount}
          expectedDepositor={dialog.depositReference}
          note="주문 상태는 그대로 유지되고, 결제 총액에 합산돼요."
          underpaymentNote="추가금 요청은 그대로 남아 구매자가 정확한 금액으로 다시 보낼 수 있어요."
          allowOverpayment
          busy={pending}
          error={error}
          onSubmit={(input) =>
            void run(() =>
              api.confirmPayment(token, order.id, {
                depositReference: input.depositorName,
                receivedAmount: input.receivedAmount,
                ...(input.overrideReason ? { depositorOverrideReason: input.overrideReason } : {}),
                ...(input.overpayment ? { overpayment: input.overpayment } : {}),
              }),
            )
          }
          onUnderpayment={(input) =>
            void run(() =>
              api.returnDeposit(token, order.id, {
                kind: DepositReturnKind.UNDERPAYMENT,
                ...input,
              }),
            )
          }
          onClose={() => {
            setDialog(null);
            setError(null);
          }}
        />
      )}
      {dialog?.kind === 'approveRefund' && (
        <ConfirmDialog
          title="환불을 승인할까요?"
          description={
            order.status === OrderStatus.PAYOUT_COMPLETED || order.status === OrderStatus.TERMINATED
              ? `${dialog.amount.toLocaleString('ko-KR')}원 환불이 승인돼요. 정산이 이미 지급된 주문이면 판매자에게 환불할 몫의 회수 요청(기한 14일)이 가고, 회수를 확인한 뒤에 송금할 수 있어요.`
              : `${dialog.amount.toLocaleString('ko-KR')}원 환불이 승인되고 지급 대기 상태가 돼요.`
          }
          confirmLabel="환불 승인"
          busy={pending}
          onConfirm={() => void run(() => api.approveRefund(token, dialog.refundId))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'completeRefund' && (
        <ConfirmDialog
          title="환불 지급을 완료 처리할까요?"
          description={`${dialog.amount.toLocaleString('ko-KR')}원을 실제로 입금했을 때만 완료 처리하세요. 원장에 환불 지급이 기록돼요.`}
          confirmLabel="지급 완료"
          danger
          busy={pending}
          onConfirm={() => void run(() => api.completeRefund(token, dialog.refundId))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'cancel' && (
        <PromptDialog
          title={
            grouped ? `묶음 주문 ${groupMembers.length}건을 모두 취소할까요?` : '주문을 취소할까요?'
          }
          description={
            grouped
              ? '한 번에 함께 주문한 묶음이라 이 주문만 취소할 수 없어요. 아래 주문이 모두 취소되고 입금 안내도 취소돼요. 취소 사유는 감사 로그에 남아요.'
              : '취소 사유는 감사 로그에 남아요.'
          }
          placeholder="취소 사유"
          submitLabel={grouped ? `${groupMembers.length}건 모두 취소` : '주문 취소'}
          danger
          busy={pending}
          onSubmit={(reason) => void run(() => api.cancelOrder(token, order.id, { reason }))}
          onClose={() => setDialog(null)}
        >
          {grouped && (
            <GroupOrdersBox
              heading={`함께 취소되는 주문 ${groupMembers.length}건`}
              orders={groupMembers}
              currentId={order.id}
            />
          )}
        </PromptDialog>
      )}
      {dialog?.kind === 'dismissDispute' && (
        <PromptDialog
          title="분쟁을 기각할까요?"
          description={
            order.settlementPaidOut
              ? '정산 지급 완료 상태로 돌아가요. 정산은 이미 지급돼 바뀌지 않아요. 사유는 구매자·판매자에게 그대로 보여요(5자 이상).'
              : order.purchaseConfirmedAt
                ? '정산 대기로 돌아가 판매자 정산이 다시 진행돼요. 사유는 구매자·판매자에게 그대로 보여요(5자 이상).'
                : '관리자 확정으로 구매확정 처리되고 판매자 정산이 만들어져요. 사유는 구매자·판매자에게 그대로 보여요(5자 이상).'
          }
          placeholder="기각 사유"
          submitLabel={order.settlementPaidOut ? '기각' : '기각하고 정산 진행'}
          minLength={5}
          busy={pending}
          onSubmit={(note) => void run(() => api.dismissDispute(token, order.id, { note }))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'resolveReport' && (
        <PromptDialog
          title={
            dialog.resolution === ViolationReportResolution.ACTIONED
              ? '위반 신고를 조치 완료로 닫을까요?'
              : '위반 신고를 기각할까요?'
          }
          description={
            dialog.resolution === ViolationReportResolution.ACTIONED
              ? '사용 중지·경고 등 한 조치를 적어 주세요. 내용은 구매자·판매자에게 그대로 보여요(5자 이상).'
              : '기각 사유는 구매자·판매자에게 그대로 보여요(5자 이상). 거래는 그대로 이어져요.'
          }
          placeholder={
            dialog.resolution === ViolationReportResolution.ACTIONED ? '처리 내용' : '기각 사유'
          }
          submitLabel={
            dialog.resolution === ViolationReportResolution.ACTIONED ? '조치 완료' : '기각'
          }
          minLength={5}
          busy={pending}
          onSubmit={(note) =>
            void run(() =>
              api.resolveViolationReport(token, order.id, dialog.reportId, {
                resolution: dialog.resolution,
                note,
              }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'lateDeposit' && (
        <LateDepositDialog
          order={order}
          token={token}
          busy={pending}
          error={error}
          onRevive={(body) => void run(() => api.reviveOrder(token, order.id, body))}
          onReturn={(body) => void run(() => api.returnDeposit(token, order.id, body))}
          onClose={() => {
            setDialog(null);
            setError(null);
          }}
        />
      )}
      {dialog?.kind === 'terminate' && (
        <TerminateContractDialog
          order={order}
          token={token}
          busy={pending}
          error={error}
          onSubmit={(body) => void run(() => api.terminateOrder(token, order.id, body))}
          onClose={() => {
            setDialog(null);
            setError(null);
          }}
        />
      )}
      {dialog?.kind === 'collectClawback' && (
        <PromptDialog
          title={`판매자 회수 ${dialog.amount.toLocaleString('ko-KR')}원을 확인할까요?`}
          description="판매자가 플랫폼 계좌로 보낸 돈을 은행 내역에서 확인한 뒤 누르세요. 원장에 회수가 기록되고, 기한 경과로 정지된 판매자 접속은 풀려요. 그다음 구매자에게 송금하고 지급 완료 처리를 해 주세요."
          placeholder="확인 내용 (예: 10/20 입금자 홍길동 90,000원)"
          submitLabel="회수 확인"
          minLength={2}
          busy={pending}
          onSubmit={(note) =>
            void run(() => api.collectPayoutClawback(token, dialog.clawbackId, { note }))
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'rejectRefund' && (
        <PromptDialog
          title="환불을 거절할까요?"
          description="거절 사유는 감사 로그에 남아요."
          placeholder="거절 사유"
          submitLabel="환불 거절"
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() => api.rejectRefund(token, dialog.refundId, { reason }))
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'cancelTermination' && termination && (
        <PromptDialog
          title="계약 해지를 취소할까요?"
          description="계약이 해지 전 상태로 돌아가요. 라이선스를 다시 쓸 수 있고, 해지하며 만든 정산·관리자 구매확정은 지워져요. 해지와 함께 정한 환불은 승인 취소(거절됨)되고 판매자 회수 요청도 함께 취소돼요. 구매자·판매자에게 사유와 함께 알림·채팅 안내가 가요. 환불을 이미 송금했다면 취소하지 마세요."
          placeholder="취소 사유 (예: 다른 주문을 해지함)"
          submitLabel="해지 취소"
          minLength={5}
          danger
          busy={pending}
          onSubmit={(reason) => void run(() => api.cancelTermination(token, order.id, { reason }))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'revokeRefund' && (
        <PromptDialog
          title={`${dialog.amount.toLocaleString('ko-KR')}원 환불 승인을 취소할까요?`}
          description="아직 송금하지 않았을 때만 취소하세요. 환불은 거절됨으로 끝나고, 구매자에게 사유와 함께 알림이 가요. 판매자 회수 요청도 함께 취소돼요(이미 회수했으면 취소할 수 없어요). 맞는 금액으로 다시 환불하려면 새로 요청해 주세요."
          placeholder="취소 사유 (예: 환불 금액을 잘못 입력함)"
          submitLabel="승인 취소"
          minLength={5}
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() => api.revokeRefundApproval(token, dialog.refundId, { reason }))
          }
          onClose={() => setDialog(null)}
        />
      )}
    </Section>
  );
}
